import { memo, useState, useRef, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep, findIndex, first, isNil, isUndefined, noop, find, isEmpty, has } from 'lodash';
import {
    ReactFlow,
    addEdge,
    isNode,
    isEdge,
    applyNodeChanges,
    applyEdgeChanges,
    Controls,
    ControlButton,
    Background,
    useReactFlow,
    useNodesInitialized,
    type ReactFlowProps,
    type Edge,
    type Node,
    type NodeChange,
    type EdgeChange,
    type ReactFlowInstance,
    BackgroundVariant,
} from 'reactflow';
import classNames from 'classnames';
// Import Types
import type { NodeOrEdge } from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { selectUserActivePartnerId } from 'store/slices/userData';
import {
    decrementBlockCount,
    incrementBlockCount,
    resetBlocksCount,
    selectCustomerJourney,
    setBlocksCounts,
} from 'store/slices/customerJourney';
// Import Constants
import { NODE_SIZES } from 'services/customerJourney/config';
import {
    l,
    layoutDirections,
    AlertTypes,
    FlowEditorElementsSetterActionTypes,
    ModalsClassNames,
    CustomerJourneyGroupBlockTypes,
} from 'constants/common';
import {
    customerJourneyFlowEditorUndoRedoMaxCount,
    deleteKeycode,
    ctrlKeyCode,
    copyKeyCode,
    pastedElementsDeviation,
    pasteKeyCode,
    modalSplashClassName,
    connectionLineType,
    maxZoom,
    minZoom,
    nodeTypes,
    edgeTypes,
    shiftKeyCode,
} from './config';
// Import Components
import { Button, Icon, Tooltip } from '@geneui/components';
import ConfirmationModal from 'components/common/Modals/ConfirmationModal';
import EditModal from './EditModal';
// Import Helpers
import {
    getLayoutedElements,
    getElementId,
    propsCollector,
    edgeBuilder,
    isGraphCyclic,
    setAutomapOptionsValues,
    newNodeNameGenerator,
    copySelectedElementsInClipboard,
    pasteElementsFromClipboard,
    cloneElements,
    setElementsLayoutPositionsElements,
    calculateTotalClientsRemoveCount,
    getTargetClientsData,
    filterFlowNodes,
    filterFlowEdges,
} from 'services/customerJourney/helpers';
// Import Hooks
import useToaster from 'hooks/UseToaster';

const { warning, success } = AlertTypes;

// Local replacement for the removed v9 `removeElements` utility.
// Removes given elements and any edges connected to removed nodes.
const removeElements = (elementsToRemove: NodeOrEdge[], elements: NodeOrEdge[]): NodeOrEdge[] => {
    const nodeIdsToRemove = new Set(filterFlowNodes(elementsToRemove).map((el) => el.id));
    const edgeIdsToRemove = new Set(filterFlowEdges(elementsToRemove).map((el) => el.id));

    return elements.filter((el) => {
        if (isNode(el)) {
            return !nodeIdsToRemove.has(el.id);
        }
        const edge = el as Edge;
        // Remove explicitly deleted edges AND edges connected to removed nodes
        return !edgeIdsToRemove.has(edge.id) && !nodeIdsToRemove.has(edge.source) && !nodeIdsToRemove.has(edge.target);
    });
};

export interface FlowEditorRef {
    deleteAction?: (elementsToRemove: Node[]) => void;
    editAction?: (node: Node) => void;
    cloneAction?: (node: Node) => void;
    editNameAction?: (updatedNode: Node) => void;
    resetFlowEditor?: () => void;
    clearFlowEditor?: () => void;
    setInitialElements?: (elements: Node[]) => void;
    /** Replaces the canvas content (e.g. with a restored draft) without touching the reset baseline */
    replaceElements?: (elements: Node[]) => void;
    setClientsCountsByBlock?: (blocksCounts: AnyObject, isAllClientsCounts?: boolean, isFiltered?: boolean) => void;
    setBlockCountCalculationTypes?: (clientsCountType: string | number, showRemovedClientsCount: boolean) => void;
    refreshElements?: () => void;
}

interface Props {
    initialElements?: Node[];
    pushElementsUpdate?: (elements: Node[]) => void;
    elementsCorrection?: (elements: Node[], params?: AnyObject) => void;
    readOnly?: boolean;
    onSelectionChange?: (elements: Node[]) => void;
    customActions?: React.ReactNode;
    customerJourneyStatus?: string;
    isFullScreen: boolean;
    onToggleFullScreen: () => void;
}

const FlowEditor = forwardRef<FlowEditorRef, Props>(
    (
        {
            pushElementsUpdate = noop,
            initialElements = [],
            readOnly = false,
            elementsCorrection = noop,
            onSelectionChange = noop,
            customActions = null,
            customerJourneyStatus: _customerJourneyStatus,
            isFullScreen,
            onToggleFullScreen,
        },
        cjPageRef,
    ) => {
        const { t } = useTranslation();
        const ActivePartnerId = useAppSelector(selectUserActivePartnerId);
        const { blocksCounts, maxBlocksCounts } = useAppSelector(selectCustomerJourney);
        const customerJourneyBlocksCountsConfigRef = useRef({ blocksCounts, maxBlocksCounts });

        useEffect(() => {
            customerJourneyBlocksCountsConfigRef.current = { blocksCounts, maxBlocksCounts };
        }, [blocksCounts, maxBlocksCounts]);

        const { showToaster } = useToaster();
        const dispatch = useAppDispatch();

        const { zoomIn, zoomOut, fitView } = useReactFlow();
        const nodesInitialized = useNodesInitialized();

        const initialElementsRef = useRef<Node[]>(isNil(initialElements) ? [] : cloneDeep(initialElements));
        const pastedCount = useRef(0);
        const reactFlowWrapper = useRef<HTMLDivElement>(null);
        const reactFlowRef = useRef<HTMLDivElement>(null);
        const selectedElements = useRef(null);

        // TODO: investigate do we need this
        useEffect(() => {
            initialElementsRef.current = isNil(initialElements) ? [] : cloneDeep(initialElements);
        }, [initialElements]);

        // Vars for layout functional
        const [layoutDirection, setLayoutDirection] = useState(layoutDirections.LR);
        const layoutDirectionRef = useRef(layoutDirection);

        // Vars for entire flow editor
        const [flowEditorInstance, setFlowEditorInstance] = useState<Nullable<ReactFlowInstance>>(null);

        const [shouldFitView, setShouldFitView] = useState(false);

        const [elements, setElements] = useState<NodeOrEdge[]>([]);

        const elementsRef = useRef(elements);
        const snapshots = useRef<{
            flowSnapshots: NodeOrEdge[][];
            currentVisibleFlowVersion: number;
        }>({ flowSnapshots: [], currentVisibleFlowVersion: -1 });

        const changeBlocksCount = (elements: NodeOrEdge[]) => {
            const _blocksCounts = {};
            elements.forEach((el) => {
                if (isNode(el)) {
                    const { type } = el;
                    _blocksCounts[type ?? ''] = isUndefined(_blocksCounts[type ?? ''])
                        ? 1
                        : ++_blocksCounts[type ?? ''];
                }
            });
            dispatch(setBlocksCounts(_blocksCounts));
        };

        // TODO: move to service delete from redux
        const changeVisibleFlowVersion = (payload) => {
            if (payload >= 0 && payload < customerJourneyFlowEditorUndoRedoMaxCount) {
                snapshots.current.currentVisibleFlowVersion = payload;

                SetElements(
                    () => {
                        const newElements = cloneDeep(
                            snapshots.current.flowSnapshots[snapshots.current.currentVisibleFlowVersion],
                        );
                        changeBlocksCount(newElements);
                        return newElements;
                    },
                    true,
                    false,
                );
            }
        };

        const updateSnapshots = (blocksCounts: AnyObject, isAllClientsCounts, isFiltered) => {
            initialElementsRef.current = initialElementsRef.current.map((element) =>
                handleElementClientsCountUpdate(
                    initialElementsRef.current,
                    element,
                    blocksCounts,
                    isAllClientsCounts,
                    isFiltered,
                ),
            );

            snapshots.current.flowSnapshots = snapshots.current.flowSnapshots.map((elements) =>
                elements.map((element) =>
                    handleElementClientsCountUpdate(elements, element, blocksCounts, isAllClientsCounts, isFiltered),
                ),
            );
        };

        const addSnapshot = (payload) => {
            if (snapshots.current.flowSnapshots.length >= customerJourneyFlowEditorUndoRedoMaxCount) {
                snapshots.current.flowSnapshots.shift();
            }
            snapshots.current.flowSnapshots.push(payload);
            snapshots.current.currentVisibleFlowVersion = snapshots.current.flowSnapshots.indexOf(payload);
        };

        const clearSnapshots = () => {
            snapshots.current.flowSnapshots = [];
            snapshots.current.currentVisibleFlowVersion = -1;
        };

        // Vars for delete logic
        const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
        const [elementsToRemove, setElementsToRemove] = useState<NodeOrEdge[]>([]);

        // Vars for edit logic
        const [isEditModalOpened, setIsEditModalOpened] = useState(false);
        const [editableBlock, setEditableBlock] = useState<Nullable<Node>>(null);

        // Vars for connecting state
        const [isConnectionInProgress, setIsConnectionInProgress] = useState(false);

        // Block popups are rendered inside the flow editor wrapper, so their events reach the canvas level
        // handlers. While one of them is opened the schema manipulations (copy, paste, delete, move, connect)
        // must stay locked, otherwise the popup inner actions conflict with the canvas ones.
        const isCanvasLocked = isEditModalOpened || isDeleteModalOpened;
        const isCanvasLockedRef = useRef(isCanvasLocked);

        useEffect(() => {
            isCanvasLockedRef.current = isCanvasLocked;
        }, [isCanvasLocked]);

        // Start undo redo functional implementation
        const [isDisabledUndo, setIsDisabledUndo] = useState(true);
        const [isDisabledRedo, setIsDisabledRedo] = useState(true);

        const handleUndo = () => {
            changeVisibleFlowVersion(snapshots.current.currentVisibleFlowVersion - 1);
        };

        const handleRedo = () => {
            changeVisibleFlowVersion(snapshots.current.currentVisibleFlowVersion + 1);
        };
        // End undo redo functional implementation

        // Start delete elements logic implementation
        const handleDeleteModalClose = () => {
            setIsDeleteModalOpened(false);
        };

        useEffect(() => {
            if (!isNil(flowEditorInstance)) {
                flowEditorInstance.fitView();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [shouldFitView]);

        const handleRemoveElementsClick = () => {
            SetElements(
                (els) => removeElements(elementsToRemove, els),
                true,
                true,
                layoutDirectionRef.current,
                {
                    type: FlowEditorElementsSetterActionTypes.RemoveElements,
                    elementsToRemove,
                },
                true,
            );

            handleDeleteModalClose();

            const _blocksCounts = {};
            elementsToRemove.forEach((el) => {
                if (isNode(el)) {
                    const { type } = el;
                    _blocksCounts[type ?? ''] = isUndefined(_blocksCounts[type ?? ''])
                        ? 1
                        : ++_blocksCounts[type ?? ''];
                }
            });
            dispatch(decrementBlockCount(_blocksCounts));
        };

        const getBlocksDeleteDataList = (elementsToRemove) => {
            const allowDataList: { name?: string }[] = [];
            const notAllowDataList: { name?: string }[] = [];
            if (!isNil(elementsToRemove)) {
                filterFlowNodes(elementsToRemove).forEach((item) => {
                    if (!isNil(item?.data?.allClientsCounts)) {
                        allowDataList.push({ name: item?.data?.name });
                    } else {
                        notAllowDataList.push({ name: item?.data?.name });
                    }

                    return {
                        name: item?.data?.name,
                        hasData: !isNil(item?.data?.allClientsCounts),
                    };
                });
            }
            return { allowDataList, notAllowDataList };
        };

        const getQuestionLabel = (elementsToRemove) => {
            const hasBlock = !isNil(find(elementsToRemove, isNode));

            if (hasBlock) {
                return getBlocksDeleteDataList(elementsToRemove).allowDataList.length > 0
                    ? l.DeleteSelectedBlocksWarningConfirmationText
                    : l.DeleteSelectedBlocksConfirmationText;
            } else {
                return l.DeleteSelectedLinesConfirmationText;
            }
        };

        const handleElementsRemove = (elementsToRemove: NodeOrEdge[]) => {
            const index = elementsToRemove.findIndex((item) => isNode(item) && item?.data?.isDeletable === false);
            if (index === -1) {
                setIsDeleteModalOpened(true);
                setElementsToRemove(elementsToRemove);
            } else {
                showToaster(warning, t(l.HasUndeletableItemsMessage));
            }
        };
        // End delete elements logic implementation

        // Start connection edges logic implementation
        const handleConnectStart = () => {
            setIsConnectionInProgress(true);
        };

        const handleConnect = ({ source, target }) => {
            const newEdge = edgeBuilder(source, target);
            const newEdges = addEdge(cloneDeep(newEdge), cloneDeep(filterFlowEdges(elements)));
            const tmpElements: NodeOrEdge[] = [...filterFlowNodes(elements), ...newEdges];

            if (isGraphCyclic(tmpElements)) {
                showToaster(warning, t(l.GraphIsCyclicMessage));
                return;
            }

            SetElements((els) => addEdge(newEdge, els));
        };

        const handleConnectEnd = () => {
            setIsConnectionInProgress(false);
            if (!isNil(flowEditorInstance)) {
                flowEditorInstance.fitView();
            }
        };
        // End connection edges logic implementation

        // Start edit block logic implementation
        const handleBlockEdit = (node: Node) => {
            if (!isNode(node)) return;
            setEditableBlock(node);
            setIsEditModalOpened(true);
        };

        const handleBlockClone = (node: Node) => {
            if (!isNode(node)) return;

            pastedCount.current++;
            const clonedElements = cloneElements(
                [node],
                elementsRef.current,
                ActivePartnerId,
                customerJourneyBlocksCountsConfigRef.current,
                (errorMessage) => {
                    showToaster(warning, t(errorMessage));
                    console.error('Cant clone elements', errorMessage);
                },
            );
            if (!isNil(clonedElements)) {
                handleElementsAdd(clonedElements, 1);
            }
        };

        const handleEditModalSave = (updatedNode: Node) => {
            SetElements((prev) => {
                return prev.map((node) => {
                    if (node.id === updatedNode.id) {
                        Object.keys(updatedNode)
                            .filter((key) => key in node)
                            .forEach((key) => {
                                node[key] = updatedNode[key];
                            });
                    }
                    return node;
                });
            }, false);
            setIsEditModalOpened(false);
        };

        const handleEditModalCancel = () => {
            // elementsRef.current.elements.forEach((node, index) => {
            //     if (node.id === prevNode.id) {
            //         elementsRef.current.elements[index] = prevNode;
            //     }
            //     return node;
            // });

            // setIsInvokeAddSnapshot(false);
            // setElements(cloneDeep(elementsRef.current.elements));

            setIsEditModalOpened(false);
        };
        // End edit block logic implementation

        // Start dragging logic implementation
        const handleDragOver = (event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
        };

        const handleDrop = (event) => {
            event.preventDefault();

            const transferredData = event.dataTransfer.getData('text/plain');
            if (isEmpty(transferredData)) {
                return;
            }

            try {
                // 1. Safe JSON Parsing
                const parsedData = JSON.parse(transferredData);

                // 2. Structural Validation (The Handshake)
                // Make sure it's an object and contains at least one unique key identifying it as YOUR block
                if (!parsedData || typeof parsedData !== 'object' || !parsedData.titleKey) {
                    console.warn('Dropped item is valid JSON, but not a valid workflow Block.');
                    return;
                }

                // Deconstruct only after verifying the data layout is correct
                const { type, titleKey, blockTitleLabel, defaultIcon, cssClassName, metaData } = parsedData;

                // 3. Fallback checks for missing nested properties (just in case)
                const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();

                // Note: React Flow 11+ renamed .project to .screenToFlowPosition,
                const position = flowEditorInstance?.project?.({
                    x: event.clientX - (reactFlowBounds?.left ?? 0),
                    y: event.clientY - (reactFlowBounds?.top ?? 0),
                }) ?? { x: 0, y: 0 };

                const id = getElementId();

                const newNode = {
                    id,
                    type,
                    position,
                    ...NODE_SIZES,
                    data: {
                        readOnly,
                        defaultIcon,
                        cssClassName,
                        blockType: titleKey,
                        actions: {
                            editNameAction: handleBlockNameEdit,
                            deleteAction: handleElementsRemove,
                            editAction: handleBlockEdit,
                            cloneAction: handleBlockClone,
                        },
                        name: newNodeNameGenerator(t, t(blockTitleLabel), elementsRef.current as Node[]),
                        metaData,
                        apiModel: {},
                        isCloneable: type !== CustomerJourneyGroupBlockTypes.target,
                    },
                };

                SetElements((es) => es.concat(newNode));

                dispatch(
                    incrementBlockCount({
                        [type]: 1,
                    }),
                );
            } catch (error) {
                // 4. Graceful Error Capture
                // This blocks syntax parsing errors from crashing the app if random text/URLs are dropped
                console.error('React Flow Drop Error: Item dropped is not valid JSON string layout.', error);
            }
        };

        const handleNodeDragStop = (_e, node) => {
            SetElements(
                (els) => {
                    els[findIndex(els, { id: node.id })] = node;
                    return [...els];
                },
                false,
                false,
            );
        };
        // End dragging logic implementation

        // Start flow layout functional
        const handleLayoutChange = useCallback(
            (direction) => {
                SetElements((prev) => prev, true, false, direction);
                setLayoutDirection(() => {
                    layoutDirectionRef.current = direction;
                    return direction;
                });
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [elements],
        );

        // End flow layout functional

        // Start init logic implementation
        const handleInit = (editorInstance: ReactFlowInstance) => {
            setFlowEditorInstance(editorInstance);
        };

        const handleBlockNameEdit = (updatedNode: Node) => {
            SetElements((prev) => {
                return prev.map((node) => {
                    if (node.id === updatedNode.id) {
                        node.data.name = newNodeNameGenerator(t, updatedNode.data.name, prev, updatedNode);
                    }
                    return node;
                });
            }, false);
        };

        const handleFlowEditorClear = () => {
            clearSnapshots();
            layoutDirectionRef.current = layoutDirections.LR;
            setLayoutDirection(() => {
                return layoutDirectionRef.current;
            });

            SetElements(() => [], true, true, layoutDirectionRef.current, null, true);
            changeBlocksCount([]);

            setIsDisabledRedo(true);
            setIsDisabledUndo(true);
        };

        const handleFlowEditorReset = () => {
            clearSnapshots();
            layoutDirectionRef.current = layoutDirections.LR;
            setLayoutDirection(() => {
                return layoutDirectionRef.current;
            });

            SetElements(
                () => cloneDeep(initialElementsRef.current),
                true,
                true,
                layoutDirectionRef.current,
                null,
                true,
            );
            changeBlocksCount(cloneDeep(initialElementsRef.current));

            setIsDisabledRedo(true);
            setIsDisabledUndo(true);
        };

        const handleElementClientsCountUpdate = (
            allElements,
            element,
            blocksClientsCount,
            isAllClientsCounts,
            isFiltered,
        ) => {
            if (!isNode(element) || !has(blocksClientsCount, element.id)) {
                return element;
            }

            const nextData = { ...element.data, isFiltered };

            if (isAllClientsCounts) {
                nextData.allClientsCounts = blocksClientsCount[element.id].OutputModelsCount;
                nextData.workingState = blocksClientsCount[element.id].WorkingState;
            }

            if (!isFiltered || !isAllClientsCounts) {
                const { targetClientsCount, targetClientsRemoveCount } = getTargetClientsData(
                    blocksClientsCount,
                    allElements,
                );

                nextData.currentClientsCounts = blocksClientsCount[element.id].OutputModelsCount;
                nextData.totalClientsCount = blocksClientsCount[element.id]?.TotalModelsCount;
                nextData.totalClientsRemoveCount = calculateTotalClientsRemoveCount(
                    element,
                    allElements,
                    blocksClientsCount,
                );
                nextData.removeClientsCounts = blocksClientsCount[element.id]?.RemovedModelsCount;
                nextData.targetClientsCount = targetClientsCount;
                nextData.targetClientsRemoveCount = targetClientsRemoveCount;
            }

            return { ...element, data: nextData };
        };

        // Handle case when CJ page want to update initial elements
        useImperativeHandle(cjPageRef, () => ({
            deleteAction(elementsToRemove: Node[]) {
                handleElementsRemove(elementsToRemove);
            },
            editAction(node: Node) {
                handleBlockEdit(node);
            },
            cloneAction(node: Node) {
                handleBlockClone(node);
            },
            editNameAction(updatedNode: Node) {
                handleBlockNameEdit(updatedNode);
            },
            resetFlowEditor() {
                handleFlowEditorReset();
            },
            clearFlowEditor() {
                handleFlowEditorClear();
            },
            setInitialElements(elements: Node[]) {
                initialElementsRef.current = cloneDeep(elements);
            },
            replaceElements(elements: Node[]) {
                clearSnapshots();
                SetElements(() => cloneDeep(elements), true, true, layoutDirectionRef.current, null, true);
                changeBlocksCount(elements);
                setIsDisabledRedo(true);
                setIsDisabledUndo(true);
            },
            setClientsCountsByBlock(blocksCounts, isAllClientsCounts = false, isFiltered = false) {
                updateSnapshots(blocksCounts, isAllClientsCounts, isFiltered);

                SetElements(
                    (els) =>
                        els.map((element) =>
                            handleElementClientsCountUpdate(els, element, blocksCounts, isAllClientsCounts, isFiltered),
                        ),
                    false,
                    false,
                );
            },
            setBlockCountCalculationTypes(clientsCountType, showRemovedClientsCount) {
                SetElements(
                    (els) =>
                        els.map((element) => {
                            if (!isNode(element)) return element;

                            return {
                                ...element,
                                data: {
                                    ...element.data,
                                    clientsCountType,
                                    showRemovedClientsCount,
                                },
                            };
                        }),
                    false,
                    false,
                );
            },
            refreshElements() {
                SetElements((prev) => prev.map((el) => el), false, false);
            },
        }));

        const handleSelectionChange = ({ nodes: selectedNodes }) => {
            if (readOnly) {
                // It means selected block can't be more then one
                onSelectionChange(first(selectedNodes));
            } else {
                // Otherwise pass array of selected blocks
                selectedElements.current = selectedNodes;
                onSelectionChange(selectedNodes);
            }
        };

        // v11 controlled-mode handlers. ReactFlow no longer mutates its own internal node/edge state
        // when controlled via `nodes` / `edges` props — we must apply the emitted changes back to our
        // local `elements` state, otherwise drag, select and dimension updates have no visible effect.
        // `remove` changes are intercepted and routed through the confirmation modal instead of applied.
        const handleNodesChange = (changes: NodeChange[]) => {
            const removeChanges = changes.filter((c) => c.type === 'remove');
            const otherChanges = changes.filter((c) => c.type !== 'remove');

            if (otherChanges.length > 0) {
                setElements((prev) => {
                    const prevNodes = filterFlowNodes(prev);
                    const prevEdges = filterFlowEdges(prev);
                    const nextNodes = applyNodeChanges(otherChanges, prevNodes);
                    const next = [...nextNodes, ...prevEdges];
                    elementsRef.current = next;
                    return next;
                });
            }

            if (!readOnly && removeChanges.length > 0) {
                const nodesToRemove = removeChanges
                    .map((c) => elementsRef.current.find((el) => el.id === (c as { id: string }).id))
                    .filter((el): el is Node => !isNil(el) && isNode(el));
                if (nodesToRemove.length > 0) {
                    handleElementsRemove(nodesToRemove);
                }
            }
        };

        const handleEdgesChange = (changes: EdgeChange[]) => {
            const removeChanges = changes.filter((c) => c.type === 'remove');
            const otherChanges = changes.filter((c) => c.type !== 'remove');

            if (otherChanges.length > 0) {
                setElements((prev) => {
                    const prevNodes = filterFlowNodes(prev);
                    const prevEdges = filterFlowEdges(prev);
                    const nextEdges = applyEdgeChanges(otherChanges, prevEdges);
                    const next = [...prevNodes, ...nextEdges];
                    elementsRef.current = next;
                    return next;
                });
            }

            if (!readOnly && removeChanges.length > 0) {
                const edgesToRemove = removeChanges
                    .map((c) => elementsRef.current.find((el) => el.id === (c as { id: string }).id))
                    .filter((el): el is Edge => !isNil(el) && isEdge(el));
                if (edgesToRemove.length > 0) {
                    handleElementsRemove(edgesToRemove);
                }
            }
        };

        const handleElementsAdd = (
            { nodes, edges }: { nodes: Node[]; edges: Edge[] },
            deviationMultiplier = pastedCount.current,
        ) => {
            const existingIds = new Set(elementsRef.current.map((el) => el.id));
            const dedupedNodes = nodes.filter((item) => !existingIds.has(item.id));

            const tmpNodes: Node[] = [];
            dedupedNodes.forEach((item) => {
                const tmpNode: Node = {
                    ...item,
                    ...NODE_SIZES,
                    selected: true,
                    position: {
                        x: item.position.x + deviationMultiplier * pastedElementsDeviation,
                        y: item.position.y + deviationMultiplier * pastedElementsDeviation,
                    },
                    data: {
                        ...item.data,
                        name: newNodeNameGenerator(t, item.data.name, [...elementsRef.current, ...tmpNodes] as Node[]),
                        allClientsCounts: null,
                        currentClientsCounts: null,
                        actions: {
                            editNameAction: handleBlockNameEdit,
                            deleteAction: handleElementsRemove,
                            editAction: handleBlockEdit,
                            cloneAction: handleBlockClone,
                        },
                    },
                };
                tmpNodes.push(tmpNode);
            });

            if (tmpNodes.length === 0) {
                return;
            }

            SetElements((prev) => {
                const deselectedElements = prev.map((el) => ({ ...el, selected: false }));
                let tmpPrevElements = deselectedElements.concat([...tmpNodes]);
                edges.forEach((item) => {
                    tmpPrevElements = addEdge(edgeBuilder(item.source, item.target), tmpPrevElements);
                });
                changeBlocksCount(tmpPrevElements);
                return cloneDeep(tmpPrevElements);
            }, false);

            handleSelectionChange({ nodes: tmpNodes });
        };

        useEffect(() => {
            SetElements(() => initialElements, true, true, layoutDirectionRef.current, null, true);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const handleKeydown = (e) => {
            // Skip the canvas level copy/paste while a popup is opened or when the event comes from a popup
            // rendered inside the wrapper, the popup has its own copy/clone actions.
            if (isCanvasLockedRef.current || !isNil(e.target?.closest?.(`.${modalSplashClassName}`))) {
                return;
            }

            if (e.keyCode === copyKeyCode && e.ctrlKey) {
                copySelectedElementsInClipboard(selectedElements.current, elementsRef.current, ActivePartnerId)
                    .then((message: string) => {
                        showToaster(success, t(message));
                    })
                    .catch((error: string) => {
                        showToaster(warning, t(error));
                    });
                pastedCount.current = 0;
            }

            if (e.keyCode === pasteKeyCode && e.ctrlKey) {
                pastedCount.current++;
                pasteElementsFromClipboard(ActivePartnerId, customerJourneyBlocksCountsConfigRef.current)
                    .then(handleElementsAdd)
                    .catch((message) => {
                        showToaster(warning, t(message));
                    });
            }
        };

        useEffect(() => {
            const reactFlowWrapperCurrent = reactFlowWrapper.current;

            if (!readOnly && !isNil(reactFlowRef.current)) {
                reactFlowWrapperCurrent?.addEventListener('keydown', handleKeydown);
            }

            return () => {
                reactFlowWrapperCurrent?.removeEventListener('keydown', handleKeydown);
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [reactFlowRef.current]);

        useEffect(() => {
            if (!isNil(flowEditorInstance)) {
                flowEditorInstance.fitView();
            }
        }, [flowEditorInstance, layoutDirection]);

        const cleanUp = () => {
            return () => {
                SetElements(() => []);
                clearSnapshots();
                dispatch(resetBlocksCount());
            };
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
        useEffect(cleanUp, []);

        const [data, setData] = useState<Nullable<{ isAddSnapshot: boolean; elements: NodeOrEdge[] }>>(null);

        useEffect(() => {
            if (!isNil(data)) {
                const { isAddSnapshot, elements } = data;
                if (isAddSnapshot) {
                    addSnapshot(cloneDeep(elements));
                }
                setIsDisabledRedo(
                    snapshots.current.currentVisibleFlowVersion === snapshots.current.flowSnapshots.length - 1,
                );
                setIsDisabledUndo(snapshots.current.currentVisibleFlowVersion === 0);

                if (!isNil(elements)) {
                    elements.forEach((el) => {
                        if (isNode(el)) {
                            el.data.collectedProperties = propsCollector(el, elements);
                            setAutomapOptionsValues(el, elements);
                        }
                    });
                }

                pushElementsUpdate(elements);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [data]);

        const SetElements = (
            setElementsFunc,
            isLayout = true,
            isAddSnapshot = true,
            _layoutDirection = layoutDirectionRef.current,
            elementsCorrectionParams: Nullable<{
                type: string;
                params?: Nullable<AnyObject>;
                elementsToRemove?: NodeOrEdge[];
            }> = { type: FlowEditorElementsSetterActionTypes.None, params: null }, // additional params for correction, type of { type: , params: }
            isLayoutNonConnected = false,
        ) => {
            setElements((prev) => {
                let tmpElements = setElementsFunc(prev);
                if (isLayout) {
                    tmpElements = getLayoutedElements(tmpElements, _layoutDirection, isLayoutNonConnected);
                    if (isLayoutNonConnected) {
                        setShouldFitView(!shouldFitView);
                    }
                } else {
                    setElementsLayoutPositionsElements(tmpElements, _layoutDirection);
                }
                elementsCorrection(tmpElements, elementsCorrectionParams); // it's not return elements, its correction elements with ref
                elementsRef.current = tmpElements;

                setData({ isAddSnapshot, elements: tmpElements }); // this is fix when react setElements calling 2 times
                return elementsRef.current;
            });
        };

        const eventHandlersProps: Partial<ReactFlowProps> = {
            onSelectionChange: handleSelectionChange,
            onNodesChange: handleNodesChange,
            onEdgesChange: handleEdgesChange,
        };
        const interactionProps: Partial<ReactFlowProps> = {};

        if (!readOnly) {
            // Event Handlers Props
            eventHandlersProps.onNodesDelete = handleElementsRemove;
            eventHandlersProps.onEdgesDelete = handleElementsRemove;
            eventHandlersProps.onConnectStart = handleConnectStart;
            eventHandlersProps.onConnectEnd = handleConnectEnd;
            eventHandlersProps.onConnect = handleConnect;
            eventHandlersProps.onDrop = handleDrop;
            eventHandlersProps.onDragOver = handleDragOver;
            eventHandlersProps.onNodeDragStop = handleNodeDragStop;
            // Interaction Props
            // ReactFlow listens the keys on the document level, so they have to be dropped while a popup is
            // opened over the canvas.
            interactionProps.deleteKeyCode = isCanvasLocked ? null : deleteKeycode;
            interactionProps.multiSelectionKeyCode = isCanvasLocked ? null : ctrlKeyCode;
            interactionProps.selectionKeyCode = isCanvasLocked ? null : shiftKeyCode;
        }

        const nodes = useMemo(() => filterFlowNodes(elements), [elements]);
        const edges = useMemo(() => filterFlowEdges(elements), [elements]);

        return (
            <div
                ref={reactFlowWrapper}
                className={classNames('crm-flow-editor-wrapper', {
                    'crm-flow-editor-connection-state': isConnectionInProgress,
                    'crm-flow-editor-horizontal-layout': layoutDirection === layoutDirections.LR,
                    'crm-flow-editor-vertical-layout': layoutDirection === layoutDirections.TB,
                    'crm-flow-editor-read-only': readOnly,
                })}
            >
                <ReactFlow
                    ref={reactFlowRef}
                    tabIndex={0}
                    maxZoom={maxZoom}
                    minZoom={minZoom}
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    connectionLineType={connectionLineType}
                    onInit={handleInit}
                    nodesDraggable={!readOnly && !isCanvasLocked}
                    nodesConnectable={!readOnly && !isCanvasLocked}
                    elementsSelectable={!isCanvasLocked}
                    disableKeyboardA11y={isCanvasLocked}
                    fitView={true}
                    style={{ visibility: nodesInitialized || nodes.length === 0 ? 'visible' : 'hidden' }}
                    {...eventHandlersProps}
                    {...interactionProps}
                >
                    <Controls
                        showZoom={false}
                        showFitView={false}
                        showInteractive={false}
                        className="crm-flow-editor-controls"
                    >
                        <Tooltip text={t(l.ZoomIn)}>
                            <ControlButton
                                className="crm-flow-editor-controls-btns"
                                onClick={() => zoomIn({ duration: 300 })}
                            >
                                <Icon type="bc-icon-zoom" />
                            </ControlButton>
                        </Tooltip>
                        <Tooltip text={t(l.ZoomOut)}>
                            <ControlButton
                                className="crm-flow-editor-controls-btns"
                                onClick={() => zoomOut({ duration: 300 })}
                            >
                                <Icon type="bc-icon-zoom-out-reset" />
                            </ControlButton>
                        </Tooltip>
                        {isFullScreen ? (
                            <Tooltip text={t(l.TurnOffFullScreen)}>
                                <ControlButton
                                    className="crm-flow-editor-controls-btns crm-flow-editor-controls-svg-btn"
                                    onClick={onToggleFullScreen}
                                >
                                    <svg
                                        className="crm-flow-editor-controls-svg-icon"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <path d="M19.72 3.215a.75.75 0 1 1 1.06 1.061L17.685 7.37h2.19a.75.75 0 0 1 .742.648l.007.102a.75.75 0 0 1-.75.75h-4a.75.75 0 0 1-.75-.75v-4a.75.75 0 0 1 1.5 0v2.189zM4.12 8.87a.75.75 0 0 1 0-1.5h2.186L3.214 4.276a.75.75 0 0 1-.072-.977l.073-.084a.75.75 0 0 1 1.06 0L7.37 6.313V4.12a.75.75 0 0 1 .648-.743l.102-.007a.75.75 0 0 1 .75.75v4a.75.75 0 0 1-.75.75zm15.72 6.25a.75.75 0 0 1 0 1.5h-2.186l3.092 3.095a.75.75 0 0 1 .073.977l-.073.084a.75.75 0 0 1-1.06 0l-3.095-3.097v2.192a.75.75 0 0 1-.649.743l-.101.006a.75.75 0 0 1-.75-.75v-4a.75.75 0 0 1 .75-.75zm-11.72.005a.75.75 0 0 1 .75.75v4a.75.75 0 0 1-1.5 0v-2.191L4.275 20.78a.75.75 0 0 1-.976.073l-.084-.073a.75.75 0 0 1 0-1.06l3.092-3.095H4.12a.75.75 0 0 1-.743-.648l-.007-.102a.75.75 0 0 1 .75-.75z" />
                                    </svg>
                                </ControlButton>
                            </Tooltip>
                        ) : (
                            <Tooltip text={t(l.TurnOnFullScreen)}>
                                <ControlButton className="crm-flow-editor-controls-btns" onClick={onToggleFullScreen}>
                                    <Icon type="bc-icon-full-screen" />
                                </ControlButton>
                            </Tooltip>
                        )}
                        <Tooltip text={t(l.FitView)}>
                            <ControlButton
                                className="crm-flow-editor-controls-btns crm-flow-editor-controls-svg-btn"
                                onClick={() => fitView({ duration: 300 })}
                            >
                                <svg
                                    className="crm-flow-editor-controls-svg-icon"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M19.5 5.5h-15a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1H9V18H4.5A2.5 2.5 0 0 1 2 15.5v-9A2.5 2.5 0 0 1 4.5 4h15A2.5 2.5 0 0 1 22 6.5v9a2.5 2.5 0 0 1-2.025 2.455 1.75 1.75 0 0 0-.69-1.116l-.339-.339h.554a1 1 0 0 0 1-1v-9a1 1 0 0 0-1-1m-8.22 4.72a.75.75 0 0 0-1.28.53v10.5a.75.75 0 0 0 1.368.425l2.467-3.588 4.26.897a.75.75 0 0 0 .685-1.264zm.22 8.615v-6.274l4.436 4.435-2.281-.48a.75.75 0 0 0-.773.31z" />
                                </svg>
                            </ControlButton>
                        </Tooltip>
                        {!readOnly && (
                            <div className="crm-flow-editor-controls-undo-redo-panel">
                                <Tooltip text={t(l.Undo)}>
                                    <ControlButton
                                        className={classNames({ disabled: isDisabledUndo })}
                                        onClick={handleUndo}
                                    >
                                        <Icon type="bc-icon-undo1" />
                                    </ControlButton>
                                </Tooltip>
                                <Tooltip text={t(l.Redo)}>
                                    <ControlButton
                                        className={classNames({ disabled: isDisabledRedo })}
                                        onClick={handleRedo}
                                    >
                                        <Icon type="bc-icon-redo1" />
                                    </ControlButton>
                                </Tooltip>
                            </div>
                        )}
                    </Controls>

                    <div className="crm-flow-editor-layout-controls">
                        {customActions}

                        <Tooltip text={t(l.IconSchemeHorizontal)}>
                            <Button
                                icon="bc-icon-scheme-horizontal"
                                className={classNames({
                                    active: layoutDirection === layoutDirections.LR,
                                    'new-section': true,
                                })}
                                onClick={() => handleLayoutChange(layoutDirections.LR)}
                            />
                        </Tooltip>
                        <Tooltip text={t(l.IconSchemeVertical)}>
                            <Button
                                icon="bc-icon-scheme-vertical"
                                className={classNames({ active: layoutDirection === layoutDirections.TB })}
                                onClick={() => handleLayoutChange(layoutDirections.TB)}
                            />
                        </Tooltip>
                    </div>

                    <Background
                        variant={BackgroundVariant.Cross}
                        gap={25}
                        size={12}
                        className="crm-cj-flow-editor-background"
                    />
                </ReactFlow>

                {isDeleteModalOpened && (
                    <ConfirmationModal
                        onOk={handleRemoveElementsClick}
                        onCancel={handleDeleteModalClose}
                        isLoading={false}
                        isVisibleModal={isDeleteModalOpened}
                        titleText={
                            !isNil(find(elementsToRemove, isNode))
                                ? t(l.DeleteSelectedBlocks)
                                : t(l.DeleteSelectedLines)
                        }
                        questionLabel={getQuestionLabel(elementsToRemove)}
                        iconType="bc-icon-delete-2"
                        actionLabel={t(l.Delete)}
                        className={ModalsClassNames.Delete}
                        isBulkAction={false}
                        {...getBlocksDeleteDataList(elementsToRemove)}
                    />
                )}

                {isEditModalOpened && (
                    <EditModal
                        block={editableBlock}
                        onOk={handleEditModalSave}
                        onCancel={handleEditModalCancel}
                        isLoading={false}
                        isVisibleModal={isEditModalOpened}
                        elements={filterFlowNodes(elementsRef.current)}
                    />
                )}
            </div>
        );
    },
);

FlowEditor.displayName = 'FlowEditor';

export default memo(FlowEditor);
